import { API_BASE } from "@/lib/constants";

export interface AppearancePrefs {
  font_size?: "small" | "regular" | "large";
  theme: "light" | "dark" | "system";
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  phone: string;
  email_verified: boolean;
  plan: string;
  credits_remaining: number;
  role: "admin" | "tech" | "readonly";
  is_platform_admin?: boolean;
  appearance?: AppearancePrefs;
  /** Saved UI language (locale code); adopted across devices when signed in. */
  language?: string;
  dismissed_academy?: string[];
  workspace?: {
    id: string;
    name: string;
    slug: string;
    logo_url: string;
    created_by: string;
    created_at: string;
  };
}

export async function saveAppearancePrefs(prefs: AppearancePrefs): Promise<void> {
  await fetch(`${API_BASE}/api/preferences/appearance`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(prefs),
  });
}

// Persist (per user) that an Academy sidebar card was dismissed, so it stays
// hidden across devices and browser-data clears.
export async function dismissAcademyCard(collectionId: string): Promise<void> {
  await fetch(`${API_BASE}/api/preferences/academy-dismiss`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ collection_id: collectionId }),
  });
}

// ── Sidebar lookup preferences ──────────────────────────────────────────────
// Users can customize which lookups appear in their sidebar. Resolution order is
// user selection → workspace default (set by an admin) → all lookups.

export interface LookupPrefs {
  /** Resolved set the sidebar should render. */
  enabled: string[];
  /** True when `enabled` comes from the user's own saved selection. */
  customized: boolean;
  /** Where `enabled` came from. */
  source: "user" | "workspace" | "default";
  /** Whether the current user may edit the workspace default. */
  is_admin: boolean;
  /** The workspace's configured default (null when unset). */
  workspace_default: string[] | null;
  has_workspace_default: boolean;
  /** Full catalog of customizable lookup ids in display order. */
  all: string[];
}

export async function getLookupPrefs(): Promise<LookupPrefs> {
  const res = await fetch(`${API_BASE}/api/preferences/lookups`, { credentials: "include" });
  if (!res.ok) throw new Error("Failed to load lookup preferences");
  return res.json();
}

export async function saveLookupPrefs(enabled: string[]): Promise<void> {
  const res = await fetch(`${API_BASE}/api/preferences/lookups`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) throw new Error("Failed to save lookup preferences");
}

export async function saveWorkspaceDefaultLookups(enabled: string[]): Promise<void> {
  const res = await fetch(`${API_BASE}/api/preferences/lookups/workspace-default`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Failed to save workspace default");
  }
}

export async function signup(name: string, email: string, password: string, referralCode?: string) {
  const body: Record<string, string> = { name, email, password };
  if (referralCode) body.referral_code = referralCode;
  const res = await fetch(`${API_BASE}/api/auth/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok && res.status !== 201) throw new Error(data.error || "Signup failed");
  return data as { message: string; verification_token: string };
}

export async function login(email: string, password: string) {
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (res.status === 403 && data.error === "email_not_verified") {
    return { needsVerification: true, verification_token: data.verification_token, message: data.message };
  }
  if (!res.ok) throw new Error(data.error || "Login failed");
  return { needsVerification: false, token: data.token, user: data.user as AuthUser };
}

export async function verifyOTP(verificationToken: string, code: string) {
  const res = await fetch(`${API_BASE}/api/auth/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ verification_token: verificationToken, code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Verification failed");
  return data as { message: string; token: string };
}

export async function resendOTP(verificationToken: string) {
  const res = await fetch(`${API_BASE}/api/auth/resend-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ verification_token: verificationToken }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to resend code");
  return data as { message: string };
}

export async function getMe(): Promise<AuthUser | null> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/me`, {
      credentials: "include",
      // Never serve a cached user - a workspace switch changes this response and
      // the browser would otherwise return the previous workspace's data.
      cache: "no-store",
    });
    if (!res.ok) return null;
    return (await res.json()) as AuthUser;
  } catch {
    return null;
  }
}

export async function logout() {
  await fetch(`${API_BASE}/api/auth/logout`, {
    method: "POST",
    credentials: "include",
  });
}

export async function forgotPassword(email: string) {
  const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data as { message: string };
}

export async function resetPassword(token: string, password: string) {
  const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Reset failed");
  return data as { message: string };
}

export async function updateProfile(name: string, phone?: string) {
  const res = await fetch(`${API_BASE}/api/auth/profile`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ name, phone: phone ?? "" }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Update failed");
  return data as { message: string };
}

export async function changePassword(currentPassword: string, newPassword: string) {
  const res = await fetch(`${API_BASE}/api/auth/change-password`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Password change failed");
  return data as { message: string };
}

export async function requestMagicLink(email: string) {
  const res = await fetch(`${API_BASE}/api/auth/magic-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data as { message: string };
}

export async function verifyMagicLink(token: string) {
  const res = await fetch(`${API_BASE}/api/auth/magic-link/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ token }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Invalid or expired link");
  return data as { token: string; user: AuthUser };
}

export async function sendActionOTP(action: "delete_account" | "leave_workspace") {
  const res = await fetch(`${API_BASE}/api/auth/action-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ action }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to send verification code");
  return data as { message: string };
}

export async function deleteAccount(code: string) {
  const res = await fetch(`${API_BASE}/api/auth/delete-account`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to delete account");
  return data as { message: string };
}

export async function leaveWorkspace(code: string) {
  const res = await fetch(`${API_BASE}/api/workspaces/leave`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to leave workspace");
  return data as { message: string };
}

export async function requestEmailChange(newEmail: string) {
  const res = await fetch(`${API_BASE}/api/auth/change-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ new_email: newEmail }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to send verification code");
  return data as { message: string };
}

export async function confirmEmailChange(newEmail: string, code: string) {
  const res = await fetch(`${API_BASE}/api/auth/change-email/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ new_email: newEmail, code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to change email");
  return data as { message: string };
}

// --- Sessions ---

export interface Session {
  id: string;
  user_id: string;
  user_agent: string;
  ip: string;
  location: string;
  current: boolean;
  last_seen_at: string;
  created_at: string;
}

export async function listSessions(): Promise<Session[]> {
  const res = await fetch(`${API_BASE}/api/auth/sessions`, {
    credentials: "include",
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to list sessions");
  return data as Session[];
}

export async function revokeSession(sessionId: string) {
  const res = await fetch(`${API_BASE}/api/auth/sessions/revoke`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ session_id: sessionId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to revoke session");
  return data as { message: string };
}

export async function revokeAllSessions() {
  const res = await fetch(`${API_BASE}/api/auth/sessions/revoke-all`, {
    method: "POST",
    credentials: "include",
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to revoke sessions");
  return data as { message: string };
}

// --- 2FA ---

export async function setup2FA() {
  const res = await fetch(`${API_BASE}/api/auth/2fa/setup`, {
    method: "POST",
    credentials: "include",
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to setup 2FA");
  return data as { secret: string; otp_uri: string };
}

export async function verify2FA(code: string) {
  const res = await fetch(`${API_BASE}/api/auth/2fa/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Verification failed");
  return data as { message: string; backup_codes: string[] };
}

export async function disable2FA(code: string) {
  const res = await fetch(`${API_BASE}/api/auth/2fa/disable`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ code }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to disable 2FA");
  return data as { message: string };
}

export async function get2FAStatus() {
  const res = await fetch(`${API_BASE}/api/auth/2fa/status`, {
    credentials: "include",
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to get 2FA status");
  return data as { enabled: boolean };
}
